import * as z from "zod";
import { CreditPackSchema } from "./charges.js";
import { Id, IsoDate } from "./ids.js";
import { PlanIdSchema } from "./plans.js";

/**
 * The three dimensions a teammate's work is metered in.
 * - `task_credits`: model work, paid for from the prepaid credit balance.
 * - `computer_minutes`: live desktops and sandboxes.
 * - `voice_minutes`: speech and calls.
 */
export const METER_DIMENSIONS = ["task_credits", "computer_minutes", "voice_minutes"] as const;
export const MeterDimensionSchema = z.enum(METER_DIMENSIONS);
export type MeterDimension = z.infer<typeof MeterDimensionSchema>;

/** The unit a dimension is counted in. Time is counted in seconds, exactly as it is used. */
export const MeterUnitSchema = z.enum(["micro_usd", "second"]);
export type MeterUnit = z.infer<typeof MeterUnitSchema>;

/** The unit of each dimension. One task credit is one USD of model work. */
export const METER_UNITS = {
  task_credits: "micro_usd",
  computer_minutes: "second",
  voice_minutes: "second",
} as const satisfies Record<MeterDimension, MeterUnit>;

/** One dimension's allowance and what is left of it. */
export const MeterQuotaSchema = z.object({
  dimension: MeterDimensionSchema,
  unit: MeterUnitSchema,
  /** Units consumed in the current period, across every space and teammate. */
  usedUnits: z.number().int().nonnegative(),
  /** The period allowance. Null when this dimension is not capped. */
  limitUnits: z.number().int().nonnegative().nullable(),
  /** What is left of the allowance. Null when there is no allowance. */
  remainingUnits: z.number().int().nullable(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  /** False when the operator does not meter this organization at all. */
  metered: z.boolean(),
});
export type MeterQuota = z.infer<typeof MeterQuotaSchema>;

/** A plan with no voice minutes shows no voice row until some is used, instead of a full red bar. */
export function hasVisibleQuota(quota: { dimension: MeterDimension; usedUnits: number; limitUnits: number | null }): boolean {
  return !(quota.dimension === "voice_minutes" && quota.limitUnits === 0 && quota.usedUnits === 0);
}


/**
 * A limit that is not an amount of metered work.
 *
 * The plan can cap how many cloud computers an organization runs at the same time. That is a
 * count, not a number of seconds, so it travels here instead of in the seconds fields.
 */
export const BillingLimitSchema = z.object({
  /**
   * `concurrent_cloud_computers`: the plan caps how many run at once. `cloud_capacity`: every
   * host is full, whatever the plan, and a slot frees as other computers stop.
   */
  kind: z.enum(["concurrent_cloud_computers", "cloud_capacity"]),
  /** How many cloud computers are up right now (for `cloud_capacity`, on all hosts). */
  active: z.number().int().nonnegative(),
  /** How many cloud computers may run at the same time (for `cloud_capacity`, on all hosts). */
  max: z.number().int().nonnegative(),
});
export type BillingLimit = z.infer<typeof BillingLimitSchema>;

/**
 * The state a teammate pauses in when a dimension runs out. The frontends turn it into a
 * recharge prompt; the reason is always the same, so it stays a closed set.
 */
export const BillingDepletedSchema = z.object({
  status: z.literal("depleted"),
  /** `quota_exhausted`: the period allowance ran out. `balance_exhausted`: the prepaid
   * balance ran out. Both mean the same thing to the teammate: ask for more. */
  reason: z.enum([
    "quota_exhausted",
    "balance_exhausted",
    /** Payment retries ran out or an operator suspended the workspace: pay or contact support. */
    "account_suspended",
    /** The 7-day trial ended: choose a plan. */
    "trial_ended",
  ]),
  dimension: MeterDimensionSchema,
  unit: MeterUnitSchema,
  usedUnits: z.number().int().nonnegative(),
  limitUnits: z.number().int().nonnegative(),
  /** Where the work was refused. */
  spaceId: Id,
  botId: Id.nullable(),
  /**
   * A local computer the workspace may use instead, when the plan's cloud time ran out.
   * Absent or null means there is none to offer. The work does not move to it by itself.
   */
  localComputer: z
    .object({
      /** The paired desktop app that would carry the work. */
      deviceId: z.string().min(1),
      name: z.string(),
      /** The teammate to move to it: bots.setComputer({ botId, mode: "local" }). */
      botId: z.string().min(1).nullable(),
      /** The person's local computer, once they have one. */
      computerId: z.string().min(1).nullable(),
    })
    .nullish(),
  /**
   * The limit that stopped the work, when the limit is a count rather than an amount of
   * metered work. Absent or null when the refusal is about a dimension that ran out.
   */
  limit: BillingLimitSchema.nullish(),
  /** Whether the deployment can take money for more, and how. */
  recharge: z.object({
    available: z.boolean(),
    methods: z.array(z.enum(["pix", "credit_card"])),
    /** What a credit pack costs, so the prompt can show a price. */
    creditPacks: z.array(CreditPackSchema),
  }),
});
export type BillingDepleted = z.infer<typeof BillingDepletedSchema>;

/** The answer to a pre-flight budget check. */
export const BillingPreflightSchema = z.discriminatedUnion("allowed", [
  z.object({ allowed: z.literal(true) }),
  z.object({ allowed: z.literal(false), depleted: BillingDepletedSchema }),
]);
export type BillingPreflight = z.infer<typeof BillingPreflightSchema>;

/** One row of a usage breakdown. */
export const MeterUsageRowSchema = z.object({
  dimension: MeterDimensionSchema,
  unit: MeterUnitSchema,
  units: z.number().int().nonnegative(),
  spaceId: Id.nullable(),
  botId: Id.nullable(),
});
export type MeterUsageRow = z.infer<typeof MeterUsageRowSchema>;

/** Where an organization's metered work went over a period. */
export const MeterUsageBreakdownSchema = z.object({
  periodStart: IsoDate,
  periodEnd: IsoDate,
  /** One row per dimension, over the whole organization. */
  totals: z.array(MeterUsageRowSchema),
  /** One row per space and dimension. */
  bySpace: z.array(MeterUsageRowSchema),
  /** One row per teammate and dimension. */
  byTeammate: z.array(MeterUsageRowSchema),
});
export type MeterUsageBreakdown = z.infer<typeof MeterUsageBreakdownSchema>;

/** What a piece of work is about to cost, in each dimension's own unit. */
export const MeterRequirementSchema = z.object({
  dimension: MeterDimensionSchema,
  units: z.number().int().nonnegative(),
});
export type MeterRequirement = z.infer<typeof MeterRequirementSchema>;

/**
 * How a Brazilian workspace sees its credit price. The rate is the deployment's own
 * configured price, never a live quote.
 */
export const BrlPricingSchema = z.object({
  /** BRL centavos for one US dollar of model credit. */
  brlCentsPerUsd: z.number().int().positive(),
  /** Where the price comes from. */
  source: z.literal("configured"),
  /** What each credit pack costs. */
  creditPacks: z.array(CreditPackSchema),
});
export type BrlPricing = z.infer<typeof BrlPricingSchema>;

/** Converts a time dimension's seconds into whole minutes, rounding up a partial minute. */
export function meterUnitsInMinutes(units: number): number {
  return Math.ceil(units / 60);
}

/** Converts micro-USD into whole cents of credit. */
export function meterUnitsInCents(units: number): number {
  return Math.ceil(units / 10_000);
}

/**
 * The share of an allowance that has been used, as a whole percent capped at 100. Null when the
 * dimension has no allowance. An allowance of zero is used up as soon as anything is used, so a
 * dimension the plan does not include shows 0 until it is used.
 */
export function usagePercent(usedUnits: number, allowanceUnits: number | null): number | null {
  if (allowanceUnits === null) return null;
  if (allowanceUnits <= 0) return usedUnits > 0 ? 100 : 0;
  return Math.min(100, Math.floor((usedUnits * 100) / allowanceUnits));
}

/**
 * The alert level a dimension has reached: 80 once 80% is used, 100 once it is used up, and 0
 * below that. A usage that jumped past 80% goes straight to the higher level.
 */
export function usageAlertLevel(usedUnits: number, allowanceUnits: number | null): 0 | 80 | 100 {
  const percent = usagePercent(usedUnits, allowanceUnits);
  if (percent === null) return 0;
  if (percent >= 100) return 100;
  if (percent >= 80) return 80;
  return 0;
}

/** One dimension of the account menu's usage summary. */
export const MeterSummaryDimensionSchema = z.object({
  dimension: MeterDimensionSchema,
  unit: MeterUnitSchema,
  /** Used in the period, in the dimension's unit (micro-USD for task credits, seconds for time). */
  usedUnits: z.number().int().nonnegative(),
  /** The plan's allowance for the period, in the same unit. Null when the dimension is not capped. */
  allowanceUnits: z.number().int().nonnegative().nullable(),
  /** Whole percent used, capped at 100. Null when the dimension is not capped. */
  percent: z.number().int().min(0).max(100).nullable(),
});
export type MeterSummaryDimension = z.infer<typeof MeterSummaryDimensionSchema>;

/** The plan, its renewal date and how much of each dimension the workspace has used. */
export const MeteringSummarySchema = z.object({
  planId: PlanIdSchema,
  planName: z.string().min(1),
  /** False when the deployment does not meter the workspace, so nothing is capped. */
  metered: z.boolean(),
  periodStart: IsoDate,
  /** When the allowances renew. */
  periodEnd: IsoDate,
  /** The highest percent of any capped dimension, or null when no dimension is capped. */
  worstPercent: z.number().int().min(0).max(100).nullable(),
  dimensions: z.array(MeterSummaryDimensionSchema),
});
export type MeteringSummary = z.infer<typeof MeteringSummarySchema>;
